import { flow, get, isEmpty } from 'lodash';
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { toastr } from 'react-redux-toastr';
import { FaSpinner } from 'react-icons/fa';
import { IStore } from '../../../../redux/IStore';
import ConsentAndIntent from '../../../ConsentAndIntent';
import { Modal as BootstrapModal } from 'react-bootstrap/lib';
import { change, reduxForm, formValueSelector } from 'redux-form';
import { IBilling, IChannel, ITraveller } from '../../../../data/api/models';
import { IBrandingState, II18nMessages } from '../../../../services/branding/models';
import { paymentMethodActions } from '../../../../services/paymentMethod/actions';
import { IPaymentMethodActions } from '../../../../services/paymentMethod/models';
import { ModalBody, ModalHeader, ModalTitle, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { IQuoteActions, IQuoteState } from '../../../../services/quote/models';
import { actions as quoteActions } from '../../../../services/quote/reducer';

interface IAddPaymentCardModalProps {
  show: boolean;
  closeModal: any;
  openCardTransactionModal: any;
  i18n: II18nMessages;
  leadTraveller: ITraveller;
  dispatch: any;
  change: any;
  channel: IChannel;
  reuseCustomerAddress: boolean;
  permissions: boolean;
  travellers: ITraveller[];
  isMta: boolean;
  showRenewal: boolean;
  quote: IQuoteState;
  branding: IBrandingState;
  actions: {
    paymentMethod: IPaymentMethodActions;
    quote: IQuoteActions;
  }
  customerId: string;
  policyId: string;
  formValues: {
    meta: {
      billing: IBilling;
      reuseCustomerAddress: boolean;
      permissions: {
        consent: boolean;
        intent: boolean;
        recurring_payments: boolean;
      }
    }
  };
  submitting: boolean,
  submitFailed: boolean,
  valid: boolean,
  handleSubmit: any;
  policyNumber?: string;
}

interface IAddPaymentCardModalState {
  isSubmitting: boolean;
  error: string;
}

class AddPaymentCardModal extends React.Component<IAddPaymentCardModalProps, IAddPaymentCardModalState> {
  constructor(props) {
    super(props);

    if (isEmpty(get(props.quote, 'channels', []))) {
      props.actions.quote.loadChannels()
    }

    this.state = {
      isSubmitting: false,
      error: '',
    };
  }

  private renderConsentAndIntent = () => {
    return (
      <ConsentAndIntent
        permissions={true}
        leadTraveller={this.props.leadTraveller}
        i18n={this.props.i18n}
        change={this.props.change}
        channel={this.props.channel}
        dispatch={this.props.dispatch}
        reuseCustomerAddress={this.props.reuseCustomerAddress}
        isMta={false}
        travellers={this.props.travellers}
        showRenewal={false}
        showSectionTitle={false}
      />
    );
  }

  private findPurchaseChannelId = (key) => {
    const { quote} = this.props;

    if (get(quote, 'channels', []).length) {
      const channel = quote.channels.filter((channel) => {
        return get(channel, 'key') === key;
      }).shift();

      if (channel) {
        return get(channel, 'id', null);
      }
    }

    return null;
  }

  private handleSubmit = () => {
    const { actions, customerId, openCardTransactionModal, formValues, closeModal } = this.props;

    if (!(formValues.meta.permissions.consent && formValues.meta.permissions.intent)) {
      this.setState({
        error: 'Unable to continue without the save card details permission',
      });
      return;
    }

    const purchaseChannelId = this.findPurchaseChannelId('admiral-b2c-portal');

    this.setState({
      isSubmitting: true,
      error: '',
    });

    actions.paymentMethod.addNewCard(customerId, purchaseChannelId, formValues, (dispatch, transactionId: string = '') => {
      closeModal();
      if (transactionId) {
        openCardTransactionModal(transactionId);
      } else {
        toastr.error('Failed to add the card');
      }

      this.setState({
        isSubmitting: false,
      });
    });
  }

  public render() {
    const { show, closeModal, handleSubmit, submitting, submitFailed, valid, policyNumber } = this.props;
    const { isSubmitting, error } = this.state;
    return (
      <BootstrapModal
        show={show}
        onHide={closeModal}
        keyboard={false}
        backdrop="static"
      >
        <ModalHeader closeButton={true}>
          <ModalTitle>{policyNumber ? 'New Card ' + policyNumber : 'New Card'}</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <Form horizontal={true} autoComplete="off" onSubmit={handleSubmit(this.handleSubmit)}>
            {this.renderConsentAndIntent()}
            {error && (
              <Alert bsStyle={'danger'}>{error}</Alert>
            )}
            <Row>
              <Col sm={12}>
                <Button
                  id="continue"
                  bsStyle="primary"
                  className="pull-right medium-width secondary-button"
                  type={'submit'}
                  disabled={submitting || (submitFailed && !valid)}
                >
                  {isSubmitting && (
                    <FaSpinner className="fa-spin"/>
                  )}
                  CONTINUE
                </Button>
              </Col>
            </Row>
          </Form>
        </ModalBody>
      </BootstrapModal>
    );
  }
}

let selector;

const ADD_FORM_NAME = 'customerAddPaymentMethodForm';

export default flow([
  reduxForm({
    form: ADD_FORM_NAME,
    destroyOnUnmount: true,
    forceUnregisterOnUnmount: true,
  }),
  (component) => {
    selector = formValueSelector(ADD_FORM_NAME);

    return component;
  },
  connect(
    (state: IStore) => {
      const reuseCustomerAddress = selector(state, 'reuseCustomerAddress');
      let permissionValue = selector(state, 'permissions');
      permissionValue = permissionValue === undefined ? true : permissionValue;

      return {
        channel: state.branding.channel,
        branding: state.branding,
        auth: state.auth,
        i18n: state.branding.i18n,
        quote: state.quote,
        reuseCustomerAddress,
        formValues: {
          meta: {
            billing: selector(state, 'billing'),
            reuseCustomerAddress,
            permissions: {
              consent: permissionValue,
              intent: permissionValue,
              recurring_payments: permissionValue,
            }
          }
        },
      };
    },
    (dispatch) => ({
      actions: {
        dispatch,
        change: bindActionCreators(change, dispatch),
        quote: bindActionCreators({ ...quoteActions }, dispatch),
        paymentMethod: bindActionCreators({ ...paymentMethodActions }, dispatch),
      }
    })
  ),
])(AddPaymentCardModal);
